import CoreService from "@/src/modules/core/services/core";
import {meService} from "@/src/modules/core/services/me";
import type {GetListResponse, Me, ParamsOptions} from "@/src/modules/core/types";

import {vehicleChecksApi} from "../api";
import {
  buildPhotoVehicleCheckCreatePayload,
  buildSkipAiVehicleCheckCreatePayload,
} from "../lib/vehicle-check-create-serializer";
import {canLinkVehicleCheckToEnforcement} from "../lib/vehicle-check-create-policy";
import {VEHICLE_CHECK_MAP_PAGE_LIMIT} from "../lib/constants";
import type {
  VehicleCheck,
  VehicleCheckConfirmPayload,
  VehicleCheckCreatePayload,
  VehicleCheckCreateResponse,
  VehicleCheckLookupPayload,
  VehicleCheckStatusPayload,
} from "../types";

class VehicleChecksService extends CoreService {
  /** History list/detail — field enforcement staff only; not PM or board of directors. */
  getCanAccessVehicleChecksHistory(me: Me | null | undefined) {
    if (!me) return false;
    if (me.isPropertyManager || me.isBoardOfDirectors) return false;
    return true;
  }

  getIsCreateEnabled() {
    return meService.getHasPermission("vehicle check store");
  }

  getIsUpdateEnabled() {
    return meService.getHasPermission("vehicle check update");
  }

  getIsShowEnabled() {
    return meService.getHasPermission("vehicle check show");
  }

  buildPhotoCreatePayload = buildPhotoVehicleCheckCreatePayload;

  buildSkipAiCreatePayload = buildSkipAiVehicleCheckCreatePayload;

  canLinkVehicleCheckToEnforcement = canLinkVehicleCheckToEnforcement;

  async getMapVehicleChecks(propertyId: number) {
    return this.getVehicleChecks({
      filter: {
        context_property_id: propertyId,
        outside_perimeter: 0,
      },
      sort: "-checked_at",
      page: {limit: VEHICLE_CHECK_MAP_PAGE_LIMIT, offset: 0},
    });
  }

  async getOutsideShiftPropertiesCount(propertyId: number) {
    const {meta} = await this.getVehicleChecks({
      filter: {
        context_property_id: propertyId,
        outside_shift_properties: 1,
      },
      page: {limit: 1, offset: 0},
    });
    return meta.total;
  }

  async createVehicleCheck(payload: VehicleCheckCreatePayload): Promise<VehicleCheckCreateResponse> {
    const {
      data: {data},
    } = await vehicleChecksApi.createVehicleCheck(payload);
    return data;
  }

  async lookupVehicleCheck(
    checkId: number,
    payload: VehicleCheckLookupPayload,
  ): Promise<VehicleCheckCreateResponse> {
    const {
      data: {data},
    } = await vehicleChecksApi.lookupVehicleCheck(checkId, payload);
    return data;
  }

  async confirmVehicleCheck(
    checkId: number,
    payload: VehicleCheckConfirmPayload,
  ): Promise<VehicleCheck> {
    const {
      data: {data},
    } = await vehicleChecksApi.confirmVehicleCheck(checkId, payload);
    return data;
  }

  async updateVehicleCheckStatus(
    checkId: number,
    payload: VehicleCheckStatusPayload,
  ): Promise<VehicleCheck> {
    const {
      data: {data},
    } = await vehicleChecksApi.updateVehicleCheckStatus(checkId, payload);
    return data;
  }

  async getVehicleChecks(options?: ParamsOptions): Promise<GetListResponse<VehicleCheck>> {
    const {data} = await vehicleChecksApi.getVehicleChecks(options);
    return {data: data.data, meta: data.meta};
  }

  async getVehicleCheck(checkId: number, options?: ParamsOptions): Promise<VehicleCheck> {
    const {
      data: {data},
    } = await vehicleChecksApi.getVehicleCheck(checkId, options);
    return data;
  }

  getIsAiPlateCorrected(check: VehicleCheck) {
    const aiPlate = check.recognition.plate?.trim().toUpperCase() ?? "";
    const confirmedPlate = check.plate?.trim().toUpperCase() ?? "";
    return Boolean(aiPlate && confirmedPlate && aiPlate !== confirmedPlate);
  }

  getBestPlateGuess(check: VehicleCheck): {plate: string; state: string} {
    return {
      plate: check.plate ?? check.recognition.plate ?? "",
      state: check.state ?? check.recognition.state ?? "",
    };
  }
}

export const vehicleChecksService = new VehicleChecksService();
